import { useEffect, useRef, useState } from "react";
import QuizHeader from "../components/QuizHeader";
import QuizQuestion from "../components/QuizQuestion";
import QuizRating from "../components/QuizRating";
import FacebookShareButton from "../components/FacebookShareButton";
import { apiUrl } from "../utils/api";
import "../style/Quiz.css";

const SAVE_ATTEMPT_API_URL = process.env.REACT_APP_SAVE_ATTEMPT_API_URL
    || apiUrl("save_attempt.php");

export default function Quiz({
    quiz,
    attemptId,
    language,
    text,
    onBack,
    onRestart,
    onRatingUpdated
}) {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [answers, setAnswers] = useState({});
    const [saveStatus, setSaveStatus] = useState("idle");
    const [saveAttemptVersion, setSaveAttemptVersion] = useState(0);
    const [isRestarting, setIsRestarting] = useState(false);
    const [restartError, setRestartError] = useState("");
    const [ratingSummary, setRatingSummary] = useState({
        averageRating: quiz.averageRating || 0,
        ratingCount: quiz.ratingCount || 0,
        userRating: quiz.userRating || 0
    });
    const saveStarted = useRef(false);
    const isComplete = currentQuestion >= quiz.questions.length;
    const quizTitle = quiz.title;

    const handleAnswer = (selectedOption) => {
        const question = quiz.questions[currentQuestion];
        setAnswers((currentAnswers) => ({
            ...currentAnswers,
            [question.id]: selectedOption
        }));
        if (selectedOption === question.CorrectAnswer) {
            setScore((currentScore) => currentScore + 1);
        }
    };

    useEffect(() => {
        if (!isComplete || saveStarted.current) {
            return;
        }
        saveStarted.current = true;
        setSaveStatus("saving");

        fetch(SAVE_ATTEMPT_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                quizId: quiz.id,
                attemptId,
                answers: quiz.questions.map((question) => ({
                    questionId: question.id,
                    answer: answers[question.id]
                }))
            })
        })
            .then(async (response) => {
                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result.error || `Attempt API returned HTTP ${response.status}`);
                }
                setScore(result.score);
                setSaveStatus("saved");
            })
            .catch((error) => {
                console.error("Failed to save quiz attempt:", error);
                setSaveStatus("error");
            });
    }, [answers, attemptId, isComplete, quiz.id, quiz.questions, saveAttemptVersion]);

    const handleNext = () => {
        setCurrentQuestion((questionIndex) => questionIndex + 1);
    };

    const handleRestart = async () => {
        setIsRestarting(true);
        setRestartError("");
        const nextAttemptId = await onRestart(quiz);
        setIsRestarting(false);
        if (!nextAttemptId) {
            setRestartError(text.startQuizError);
            return;
        }
        setCurrentQuestion(0);
        setScore(0);
        setAnswers({});
        setSaveStatus("idle");
        saveStarted.current = false;
    };

    const handleBack = () => {
        setCurrentQuestion(0);
        setScore(0);
        onBack();
    };

    return (
        <div className="quiz">
            <div className="quiz-card">
                {isComplete ? (
                    <div className="quiz-results" aria-live="polite">
                        <h1>{quizTitle} {text.complete}</h1>
                        <p>
                            {text.youScored} {score} {text.outOf} {quiz.questions.length}.
                        </p>
                        <p className="attempt-save-status" role="status">
                            {saveStatus === "saving" && text.savingScore}
                            {saveStatus === "saved" && text.scoreSaved}
                            {saveStatus === "error" && text.scoreSaveError}
                        </p>
                        <section className="quiz-result-rating" aria-label={text.rateQuiz}>
                            <h2>{text.rateQuiz}</h2>
                            <QuizRating
                                quizId={quiz.id}
                                averageRating={ratingSummary.averageRating}
                                ratingCount={ratingSummary.ratingCount}
                                userRating={ratingSummary.userRating}
                                text={text}
                                onRated={(result) => {
                                    setRatingSummary({
                                        averageRating: result.averageRating,
                                        ratingCount: result.ratingCount,
                                        userRating: result.userRating
                                    });
                                    onRatingUpdated(quiz.id, result);
                                }}
                            />
                        </section>
                        <FacebookShareButton title={quizTitle} text={text} />
                        {restartError && <p className="auth-inline-error" role="alert">{restartError}</p>}
                        {saveStatus === "error" && (
                            <button
                                className="quiz-secondary-btn"
                                onClick={() => {
                                    saveStarted.current = false;
                                    setSaveStatus("idle");
                                    setSaveAttemptVersion((version) => version + 1);
                                }}
                            >
                                {text.retrySave}
                            </button>
                        )}
                        <button
                            className="quiz-btn"
                            type="button"
                            disabled={isRestarting}
                            onClick={handleRestart}
                        >
                            {isRestarting ? text.startingQuiz : text.tryAgain}
                        </button>
                        <button className="quiz-secondary-btn" type="button" onClick={handleBack}>
                            {text.chooseAnotherQuiz}
                        </button>
                    </div>
                ) : (
                    <>
                        <QuizHeader
                            score={score}
                            currentQuestion={currentQuestion}
                            totalQuestions={quiz.questions.length}
                            title={quizTitle}
                            schoolTrack={quiz.schoolTrack}
                            subject={quiz.category}
                            language={language}
                            text={text}
                        />
                        <QuizQuestion
                            key={`${quiz.id}-${quiz.questions[currentQuestion].id}`}
                            quizId={quiz.id}
                            question={quiz.questions[currentQuestion]}
                            language={language}
                            isLastQuestion={currentQuestion === quiz.questions.length - 1}
                            onAnswer={handleAnswer}
                            onNext={handleNext}
                            text={text}
                        />
                    </>
                )}
                {!isComplete && (
                    <button className="quiz-back-btn" onClick={handleBack}>
                        {text.backToQuizzes}
                    </button>
                )}
            </div>
        </div>
    );
}
